/* eslint-disable no-console */
import {Divider, Flex} from 'antd';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend
} from 'recharts';

import {eachWordCap} from '../../../../utils/FormUtils/FirstCap';

import {ScenarioEnum} from './IndustryBenchmarks/utils';

const ReductionChart: React.FC<{
  selectedYear: string;
  reductionData: any;
  reductionTimelineData: any;
}> = ({selectedYear, reductionData, reductionTimelineData}) => {
  const yMax = reductionData?.length
    ? reductionData?.reduce(
        (
          max: number,
          d: {expectedEmissions?: number; actualEmissions?: number}
        ) => {
          const expected = d.expectedEmissions ?? 0;
          const actual = d.actualEmissions ?? 0;

          return Math.max(max, expected, actual);
        },
        0
      )
    : 0;

  const sbti = 'sbti';

  const reductioNPerYearLabel =
    reductionTimelineData?.selectScenario ===
    ScenarioEnum.ABSOLUTE_WELL_BELOW_2C
      ? '2.5 % '
      : '4.2 % ';

  return (
    <div className="reduction-chart-wrapper">
      <div className="reduction-chart-header">
        <Flex className="totalGHGEmissionsTitle">
          {reductionTimelineData?.selectReductionTarget === sbti
            ? reductionTimelineData?.selectScenario
            : eachWordCap(reductionTimelineData?.selectReductionTarget ?? '')}
        </Flex>
        <div className="reduction-chart-legend-spacer" aria-hidden />
        {reductionTimelineData?.selectReductionTarget === sbti &&
          reductionTimelineData !== null && (
            <div className="reduction-per-year-block">
              <span className="reduction-per-year-value">
                <span className="reduction-per-year-arrow">↓</span>
                {reductioNPerYearLabel}
              </span>
              <span className="reduction-per-year-label">
                Reduction per year
              </span>
            </div>
          )}
      </div>
      <Divider />
      {reductionTimelineData && (
        <div
          style={{
            textAlign: 'right',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: '10px',
            justifyContent: 'flex-end',
            gap: '10px',
            marginRight: '30px',
            fontWeight: 500,
            fontSize: 12,
            fontSmooth: 'always'
          }}
        >
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#20E2BD',
                borderRadius: '50%'
              }}
            ></div>
            <span
              style={{
                fontSize: '10px',
                color: '#557F97',
                marginLeft: '5px'
              }}
            >
              Expected Reduction
            </span>
          </div>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#9fd983',
                borderRadius: '50%'
              }}
            ></div>
            <span
              style={{
                fontSize: '10px',
                color: '#557F97',
                marginLeft: '5px'
              }}
            >
              Actual Reduction
            </span>
          </div>
        </div>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart
          data={reductionData}
          margin={{top: 40, right: 20, bottom: 40, left: 40}}
        >
          <defs>
            <linearGradient
              id="reductionExpectedFill"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor="#024064" stopOpacity={1} />
              <stop offset="100%" stopColor="#ffffff" stopOpacity={0.45} />
            </linearGradient>
            <linearGradient
              id="reductionActualFill"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor="#05DFAD" stopOpacity={1} />
              <stop offset="100%" stopColor="#ffffff" stopOpacity={0.45} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="year"
            padding={{left: 30, right: 30}}
            label={{
              value: 'Year',
              position: 'insideBottom',
              offset: -10,
              style: {
                fontSize: '14px',
                fontWeight: 'bold'
              }
            }}
          />
          <YAxis
            label={{
              value: 'tCO2e',
              angle: -90,
              position: 'insideLeft',
              offset: -5,
              dy: 23,
              style: {
                fontSize: '14px',
                fontWeight: 'bold'
              }
            }}
          />
          <Tooltip
            labelFormatter={(label) => `Year : ${label}`}
            formatter={(value: number) => `${value.toFixed(2)}  tCO2e`}
          />
          <Legend
            verticalAlign="top"
            align="right"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{opacity: 0, pointerEvents: 'none'}}
          />
          <Area
            type="linear"
            dataKey="expectedEmissions"
            name="Expected Reduction"
            stroke="#20E2BD"
            fill="#EEF2FF80"
            strokeWidth={2}
          />
          <Area
            type="linear"
            dataKey="actualEmissions"
            name="Actual Reduction"
            stroke="#9fd983"
            fill="#EEF2FF80"
            strokeWidth={2}
          />
          <ReferenceLine x={selectedYear.toString()} stroke="#AABCC7" />
          <ReferenceLine y={yMax / 2} stroke="#ADAFB7" strokeDasharray="3 3" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ReductionChart;
